import type { Immutable } from 'immer';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { UnionOf } from 'ts-toolbelt/out/Object/UnionOf';
import { LAUNCHDARKLY_TOGGLES } from '@studio/constants/launchdarkly-toggle-constants';
import OpaPermissionValues from '@studio/constants/opa-permissions';
import type Permissions from '@studio/constants/permissions';
import { authenticationOpaPermissionsAtom } from '@studio/storeV2/authentication';
import { useSession } from '@studio/storeV2/authentication/session-store';

type OpaPermission = UnionOf<typeof OpaPermissionValues>;

/**
 * Permissions in this set bypass the OPA_MIGRATION flag and are enforced immediately.
 * This is used for permissions that don't have a legacy equivalent or are safe to enable early.
 * Can be removed when OPA migration is complete and the LAUNCHDARKLY_TOGGLES.OPA_MIGRATION flag is no longer needed.
 */
const OPA_BYPASS_PERMISSIONS = new Set<OpaPermission>([
  OpaPermissionValues.BILLING_UPDATE,
  OpaPermissionValues.ENCODER_CHANNEL_USER_READ,
  OpaPermissionValues.ENCODER_CHANNEL_USER_UPDATE,
  OpaPermissionValues.POST_CREATE,
  OpaPermissionValues.POST_READ,
]);

const useIsAuthorized = (
  requiredPerms: Immutable<Permissions[]>,
  includesAny?: boolean,
  requiredOpaPerms?: Immutable<OpaPermission[]>
): boolean => {
  const { [LAUNCHDARKLY_TOGGLES.OPA_MIGRATION]: opaMigration } = useFlags();
  const { capabilities } = useSession();
  const { data: opaPermissions } = useAtomValue(authenticationOpaPermissionsAtom);
  const shouldIgnoreOpaFlag = requiredOpaPerms?.some((permission) => OPA_BYPASS_PERMISSIONS.has(permission)) ?? false;
  const shouldUseOpa = Boolean(requiredOpaPerms?.length) && (opaMigration || shouldIgnoreOpaFlag);

  const currentList = shouldUseOpa ? opaPermissions : capabilities;
  const requiredList = shouldUseOpa ? requiredOpaPerms : requiredPerms;

  if (!currentList) return false;
  if (!requiredList?.length) return true;
  if (includesAny) {
    return requiredList.some((permission) => currentList.includes(permission as string));
  }

  return requiredList.every((permission) => currentList.includes(permission as string));
};

export default useIsAuthorized;
